import clsx from 'clsx'
import NextImage from 'next/legacy/image'
import Link from 'next/link'
import { useCallback, useEffect, useState, type ChangeEvent, type FunctionComponent } from 'react'

import { useAllProductIds } from '../../../hooks/useAllProductIds'
import { useIsAllowedToFullUse } from '../../../hooks/useIsAllowedToFullUse'
import { getDailyDosePricePerPackage, getDefinedDailyDoseOfProduct } from '../../const/DailyEnergyRequirementsHelper'
import { DAYS_PER_MONTH, MAX_DAYS_PACK_OPENED } from '../../const/digitContants'
import { formatPackLabel } from '../../const/DosageHelper'
import { InternalLink } from '../../const/Links'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import type { ConsumptionMessageResult } from '../../data/ConsumptionMessageFragment'
import type { ProductDetailResult } from '../../data/ProductFragment'
import type { ProductPackageResult } from '../../data/ProductPackage'
import SvgChevronDown from '../../svg/icons/ChevronDown'
import { useActiveDog, useDER } from '../../user/ActiveDogContext'
import { useAuth } from '../../user/UserContext'
import { RadioInput } from '../form/RadioInput'
import { Button } from '../shared/Button'
import { CompareButton } from '../shared/CompareButton'
import { Container } from '../shared/Container'
import { HeurekaButton } from '../shared/HeurekaButton'
import { Hidden } from '../shared/Hidden'
import { InfoBar } from '../shared/InfoBar'
import { LoaderDailyDose } from '../shared/LoaderDailyDose'
import { Message } from '../shared/Message'
import { Modal } from '../shared/Modal'
import { NotSpecifiedAlert } from '../shared/NotSpecifiedAlert'
import { Tag } from '../shared/Tag'
import style from './ProductIntroSummary.module.sass'
import { ProductIntroSummarySticky } from './ProductIntroSummarySticky'

type ProductIntroSummaryProps = {
	product: ProductDetailResult
	consumptionMessages: ConsumptionMessageResult[]
	openModal: () => void
}

export const ProductIntroSummary: FunctionComponent<ProductIntroSummaryProps> = ({
	product,
	consumptionMessages,
	openModal,
}) => {
	const [isRadioModalOpen, setIsRadioModalOpen] = useState(false)

	const sortedPackagings = product.packagings.sort((a, b) => Number(a.amount) - Number(b.amount))

	const [radioValue, setRadioValue] = useState<ProductPackageResult>(sortedPackagings[0])
	const [startCatchRef, setStartCatchRef] = useState<null | HTMLDivElement>(null)

	const setValue = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const pack = product.packagings.find((pack) => pack.id === event.target.value)
			if (pack) {
				setRadioValue(pack)
			}
		},
		[product.packagings],
	)

	useEffect(() => {
		const pack = product.packagings.find((pack) => pack.id === radioValue)
		if (pack) {
			setRadioValue(pack)
		}
	}, [product.packagings, radioValue])

	const { dog } = useActiveDog()

	const auth = useAuth()
	const loading = auth.status === 'loading'
	const isAllowedToFullUse = useIsAllowedToFullUse()
	const getDailyEnergyRequirement = useDER()

	const der = Number(getDailyEnergyRequirement?.data?.dailyEnergyRequirement)

	const [packageValue, setPackageValue] = useState(sortedPackagings[0]?.id)

	const packageSelected = sortedPackagings.find((packaging) => packaging.id === packageValue)
	const translate = useTranslate()

	const isMultiplePack = radioValue.label?.includes('x')

	const getWarningMessage = useCallback(
		(dailyDose: number, pack: ProductPackageResult) => {
			const price = pack.heurekaPrice ?? pack.price
			if (pack.amount && price && pack.label) {
				const isMultiplePack = pack.label.includes('x')
				const singlePackSizeArray = pack.label.split('x').map((val) => val.trim())
				const getSinglePackAmount = isMultiplePack ? Number(singlePackSizeArray[1].replace(',', '.')) : pack.amount
				const packSize = getSinglePackAmount * 1000 // to grams
				const lasting = Math.round(packSize / dailyDose)

				if (lasting > MAX_DAYS_PACK_OPENED) {
					return <InfoBar type="warningRed" label={translate('getMessage.packIsOpen')} />
				}
			}
		},
		[translate],
	)

	const getRadioMessage = useCallback(
		(dailyDose: number, pack: ProductPackageResult, consumptionMessages: ConsumptionMessageResult[]) => {
			const price = pack.heurekaPrice ?? pack.price
			if (pack.amount && price && pack.label) {
				const singlePackSizeArray = pack.label.split('x').map((val) => val.trim())
				const isMultiplePack = pack.label.includes('x')

				const getSinglePackAmount = isMultiplePack ? Number(singlePackSizeArray[1].replace(',', '.')) : pack.amount

				const packSize = getSinglePackAmount * 1000 // to grams

				const lasting = isMultiplePack
					? Math.round((packSize * Number(singlePackSizeArray[0])) / dailyDose)
					: Math.round(packSize / dailyDose)

				const message =
					consumptionMessages.find((message) => lasting === message.numberOfDays) ??
					consumptionMessages[consumptionMessages.length - 1]

				if (lasting === 0) {
					return translate('productBanner.packageLast.lessThanDay')
				}
				return `${translate('productBanner.packageLast.for')} ${message.finalWording}`
			}
		},
		[translate],
	)

	const infoMessage =
		radioValue &&
		dog &&
		product.metabolizedEnergy &&
		getWarningMessage(getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy), radioValue)

	const allProductsStats = useAllProductIds(product.id)

	const indexNumber =
		typeof allProductsStats.productIndex === 'number' && allProductsStats.productIndex >= 0
			? allProductsStats.productIndex + 1
			: undefined

	const label =
		indexNumber && indexNumber <= 3 ? `${translate('productIntroSummary.tag.top')} ${indexNumber}` : `${indexNumber}.`

	const dailyDose = product.metabolizedEnergy && getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy)

	const getDailyPrice =
		dailyDose &&
		packageSelected &&
		getDailyDosePricePerPackage(der, packageSelected.amount, product.metabolizedEnergy, packageSelected.price) === 0
			? 1
			: dailyDose &&
				packageSelected &&
				getDailyDosePricePerPackage(der, packageSelected.amount, product.metabolizedEnergy, packageSelected.price)

	const getPricePerMonth = Number(getDailyPrice) * DAYS_PER_MONTH

	const alternativeText = product.productLine?.name
		? `${product.brand?.name} ${product.productLine?.name} ${product.name}`
		: `${product.brand?.name} ${product.name}`

	const packagingsRadio = (
		<>
			{sortedPackagings.length > 0 && (
				<div className={style.radioGroupWrapper}>
					<div className={style.radioTitle}>
						<Translation tr="productIntroSummary.weight" />
					</div>
					<div className={style.radioGroup} onChange={setValue}>
						{sortedPackagings.map((pack) => (
							<RadioInput
								key={pack.id}
								label={
									<>
										{formatPackLabel(pack.label)}
										{(isAllowedToFullUse || allProductsStats.isTopFirst || allProductsStats.isFed) &&
										dailyDose &&
										dailyDose > 0 ? (
											<span className={clsx(style.infoSmallMessage, style.infoSmallMessageDays)}>
												{product.metabolizedEnergy &&
													getRadioMessage(
														getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy),
														pack,
														consumptionMessages,
													)}
											</span>
										) : undefined}
									</>
								}
								name="packaging"
								value={pack.id}
								onChange={() => setPackageValue(pack.id)}
								inGrid
								checked={pack.id === packageValue}
							/>
						))}
					</div>
					<div className={style.infoBarsWrapper}>
						{isMultiplePack && <InfoBar type="infoYellow" label={translate('getMessage.packIsMultiple')} />}

						{(infoMessage || isMultiplePack) &&
						(isAllowedToFullUse || allProductsStats.isTopFirst || allProductsStats.isFed) &&
						dailyDose &&
						dailyDose > 0 ? (
							<div className={style.infoMessage}>{infoMessage}</div>
						) : (
							''
						)}
					</div>
				</div>
			)}
		</>
	)

	const title = (
		<h1 className={style.titleRoot}>
			<span className={style.titleWrapper}>
				{product.productLine ? (
					<Link
						href={`${InternalLink.ProductLine}${product.productLine?.slug}`}
						title={alternativeText}
						className={style.link}
					>
						<span className={clsx(style.title, style.naming)}>
							{product.brand?.name} {product.productLine?.name}
						</span>
					</Link>
				) : (
					<Link href={`${InternalLink.Brand}${product.brand?.slug}`} title={alternativeText} className={style.link}>
						<span className={clsx(style.title, style.naming)}>{product.brand?.name}</span>
					</Link>
				)}
			</span>{' '}
			<span className={style.subtitle}>{product.name}</span>
		</h1>
	)

	return (
		<div className={style.root}>
			<ProductIntroSummarySticky
				package={packageSelected}
				product={product}
				state={[startCatchRef, setStartCatchRef] as const}
			/>
			<Container size="wide" variant="leftAligned" disableGutters>
				<div className={style.wrapper}>
					<div className={style.preview}>
						{((indexNumber && (isAllowedToFullUse || allProductsStats.isFed || allProductsStats.isTopFirst)) ||
							allProductsStats.isFed) && (
							<div className={style.tagWrapper}>
								<Tag
									color={Number(indexNumber) <= 3 ? 'greenLight' : allProductsStats.isFed ? 'pink' : 'grey'}
									isLarger
									label={
										allProductsStats.isFed
											? indexNumber
												? `${translate('productBanner.tag.fedProduct')}, ${indexNumber}.`
												: translate('productBanner.tag.fedProduct')
											: label
									}
								/>
							</div>
						)}
						<div className={style.headTitle}>
							<div className={style.imageWrapper}>
								<NextImage
									src={product.heurekaImage?.url ?? product.image?.url ?? '/images/placeholder-product.png'}
									title={alternativeText}
									alt={translate('alternativeText.productImage')}
									layout="fill"
									objectFit="contain"
								/>
							</div>
							<Hidden breakpoint="desktopUp">{title}</Hidden>
						</div>
						{sortedPackagings.length > 1 && (
							<Hidden breakpoint="mobileUp">
								<div className={style.radioTitle}>
									<Translation tr="productIntroSummary.weight" />
								</div>
								<div className={style.thumbnailFoot}>
									<button className={style.thumbnailRadioButton} onClick={() => setIsRadioModalOpen(true)}>
										<span className={style.thumbnailRadioButtonIcon}>
											<SvgChevronDown />
										</span>
										&nbsp;
										<div className={style.thumbnailLabel}>
											{formatPackLabel(packageSelected?.label) || formatPackLabel(sortedPackagings[0].label)}
										</div>
										{(isAllowedToFullUse || allProductsStats.isTopFirst || allProductsStats.isFed) &&
										packageSelected &&
										dailyDose &&
										dailyDose > 0 ? (
											<div className={clsx(style.infoSmallMessage, style.infoSmallMessageDays)}>
												{product.metabolizedEnergy &&
													getRadioMessage(
														getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy),
														packageSelected,
														consumptionMessages,
													)}
											</div>
										) : undefined}
									</button>
								</div>

								{isMultiplePack && (
									<div className={clsx(style.infoSmallMessage)}>
										<InfoBar type="infoYellow" label={translate('getMessage.packIsMultiple')} />
									</div>
								)}
								{dog &&
									product.metabolizedEnergy &&
									getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy) > 0 &&
									infoMessage && (
										<div className={clsx(style.infoSmallMessage)}>
											<InfoBar type="warningRed" label={translate('getMessage.packIsOpen')} />
										</div>
									)}
							</Hidden>
						)}
						<Hidden breakpoint="mobileDown">
							<div className={style.compareButtonWrapper}>
								<CompareButton id={product.id} openModal={openModal} />
							</div>
						</Hidden>
					</div>
					<div className={style.body}>
						<div className={style.content}>
							<Hidden breakpoint="desktopDown">{title}</Hidden>
							{sortedPackagings.length > 1 ? (
								<Hidden breakpoint="mobileDown">{packagingsRadio} </Hidden>
							) : (
								packagingsRadio
							)}
						</div>
						<Hidden breakpoint="desktopUp">
							<div className={style.catch} ref={setStartCatchRef} />
						</Hidden>
						<div className={style.summary}>
							{loading && <LoaderDailyDose />}
							<div className={loading ? style.notVisible : style.alert}>
								{der ? (
									isAllowedToFullUse || allProductsStats.isTopFirst || allProductsStats.isFed ? (
										<div className={style.guidelineDailyAmountWrapper}>
											<span className={style.titleGuidelineDailyAmount}>
												<Translation tr="productIntroSummary.dailyDose" />
											</span>
											{typeof product.metabolizedEnergy === 'number' && product.metabolizedEnergy >= 0 ? (
												<>
													<div className={style.guidelineDailyAmount}>
														{getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy)}{' '}
														<Translation tr="dose.gram.shortcut" /> <Translation tr="productIntroSummary.from" />{' '}
														{getDailyPrice} <Translation tr="productIntroSummary.currency" />
													</div>
													{getPricePerMonth && (
														<div className={style.priceCalculation}>
															{' '}
															<Translation tr="dailyDose.symbol" /> {getPricePerMonth}{' '}
															<Translation tr="productIntroSummary.currencyMonthly" />
														</div>
													)}{' '}
												</>
											) : (
												<div className={style.messageEmpty}>
													<Message
														icon={<span className={style.infoIcon}>i</span>}
														text={
															<>
																<Translation tr="productIntroSummary.message.missingInfo.first" />
																<br />
																<Translation tr="productIntroSummary.message.missingInfo.second" />
															</>
														}
													/>
												</div>
											)}
										</div>
									) : (
										<>
											<span
												className={clsx(style.titleGuidelineDailyAmount, style.titleGuidelineDailyAmountMarginBottom)}
											>
												<Translation tr="productIntroSummary.dailyDose" />
											</span>
											<NotSpecifiedAlert color="grey" notSigned />
										</>
									)
								) : (
									<>
										<span
											className={clsx(style.titleGuidelineDailyAmount, style.titleGuidelineDailyAmountMarginBottom)}
										>
											<Translation tr="productIntroSummary.dailyDose" />
										</span>
										<NotSpecifiedAlert color="grey" />
									</>
								)}
							</div>
							<div className={style.buttonWrapper}>
								<HeurekaButton
									id={packageSelected?.positionId}
									link={packageSelected?.heurekaLink ?? packageSelected?.link}
								/>

								<Hidden breakpoint="mobileUp">
									<CompareButton id={product.id} openModal={openModal} isLeftAligned />
								</Hidden>
							</div>
						</div>
					</div>
				</div>
				{isRadioModalOpen && (
					<Modal close={() => setIsRadioModalOpen(false)} isOpen={isRadioModalOpen} type="filter">
						<div className={style.infoTitle}>
							<Translation tr="productIntroSummary.weight" />
						</div>
						<div className={style.radioMobileGroup} onChange={setValue}>
							{sortedPackagings.length > 0 &&
								sortedPackagings.map((pack) => (
									<>
										<RadioInput
											key={pack.id}
											label={
												<>
													<span className={style.numberLabel}>{formatPackLabel(pack.label)}</span>
													{(isAllowedToFullUse || allProductsStats.isTopFirst || allProductsStats.isFed) &&
													dailyDose &&
													dailyDose > 0 ? (
														<div className={style.infoSmallMessage}>
															{product.metabolizedEnergy &&
																getRadioMessage(
																	getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy),
																	pack,
																	consumptionMessages,
																)}
														</div>
													) : undefined}
												</>
											}
											name="packaging"
											value={pack.id}
											checked={pack.id === packageValue}
											onChange={() => setPackageValue(pack.id)}
											inGrid
											inColumn
										/>
										{loading && (
											<div className={style.loaderInfo}>
												<span className={style.infoIcon}>i</span>
												<span className={style.loaderMessage} />
											</div>
										)}
									</>
								))}
							{isMultiplePack && (
								<div className={clsx(style.infoYellow, style.info)}>
									<InfoBar type="infoYellow" label={translate('getMessage.packIsMultiple')} />
								</div>
							)}
							{infoMessage &&
								dog &&
								product.metabolizedEnergy &&
								getDefinedDailyDoseOfProduct(der, product.metabolizedEnergy) > 0 && (
									<div className={style.info}>{infoMessage}</div>
								)}
						</div>
						<div className={style.closeButtonWrapper}>
							<Button
								label={translate('productIntroSummary.button.close')}
								onClick={() => setIsRadioModalOpen(false)}
							/>
						</div>
					</Modal>
				)}
			</Container>
			<Hidden breakpoint="desktopDown">
				<div className={style.catch} ref={setStartCatchRef} />
			</Hidden>
		</div>
	)
}
